/**
 * The Maze TD simulation. A faithful port of the frozen prototype's sim
 * (prototypes/classic-html/gemtd-classic.html): same state, same update order,
 * same RNG call sites, same float arithmetic. Bit-exactness is enforced by the
 * golden replay suite; any change that desyncs it is a defect or a deliberate,
 * ADR-backed balance change that re-records the corpus.
 *
 * Pure and IO-free: no DOM, no Date, no Math.random. Presentation effects are
 * emitted as GameEvents and drained by the shell.
 */
import {
  CELL,
  CHANCE_COSTS,
  CHANCE_TABLE,
  DT,
  ENDLESS,
  FLY_ROUTE,
  GEM_KEYS,
  GEMS,
  GREATER_SLATES,
  LAST_WAVE,
  NX,
  NY,
  RECIPES,
  REPLAY_VERSION_3,
  SLATE_ANCHOR_TIER,
  SLATE_FODDER_COUNT,
  SLATE_FODDER_TIER,
  SLATE_KEYS,
  SLATE_MIN_DIST,
  SLATE_RECIPES,
  SLATES,
  SPAWN_GAP,
  SPECIALS,
  SPITE_RADIUS,
  START_GOLD,
  START_LIVES,
  TIER_MULT,
  TIERS,
  WAYPOINT_SET,
  WAYPOINTS,
  aquaCd,
  poisonDps,
  slowF,
  type GemTypeKey,
  type SlateKey,
  type SlateRecipe,
  type SpecialKey,
} from '../data/balance.js';
import { auraBonusFor, collectAuraSources, type AuraSource } from './aura.js';
import {
  collectSlates,
  creepZone,
  gemSlateBuffs,
  spiteFor,
  type CreepZone,
  type SlateInstance,
} from './slates.js';
import { computeBalanceHash } from './balanceHash.js';
import { Djb2 } from './hash.js';
import { dist } from './dist.js';
import { computePath } from './path.js';
import { Mulberry32 } from './rng.js';
import { waveDef } from './waves.js';
import type {
  BaseGemCell,
  Cell,
  Creep,
  GameEvent,
  GemCell,
  GemStats,
  GridPoint,
  Phase,
  PlayerKind,
  Projectile,
  Replay,
  RunOutcome,
  WaveDef,
} from './types.js';

export type GameMode = 'record' | 'replay';

/**
 * Semantics switches derived from the replay format version (ADR 0007).
 * v1/v2 recordings were made when the run ended at wave 50; they re-simulate
 * under that legacy rule so the pre-endless corpus and leaderboard replays
 * stay bit-exact. Live play always uses current (v3) rules.
 */
export interface GameRules {
  readonly endsAtLastWave: boolean;
}

export const CURRENT_RULES: GameRules = { endsAtLastWave: false };

export interface GameOptions {
  readonly seed: number;
  /** ISO timestamp supplied by the shell; the core never reads a clock. */
  readonly startedAt: string;
  readonly player?: PlayerKind;
  readonly mode?: GameMode;
  readonly rules?: GameRules;
}

interface PendingRoll {
  readonly type: GemTypeKey;
  readonly tier: number;
}

export class Game {
  readonly mode: GameMode;
  readonly rules: GameRules;
  readonly grid: (Cell | null)[][];
  gold = START_GOLD;
  lives = START_LIVES;
  wave = 1;
  chanceLvl = 0;
  phase: Phase = 'build';
  phaseTimer = 0;
  newGems: GemCell[] = [];
  pendingRolls: PendingRoll[] = [];
  /**
   * The move mechanic's one-slot hand: a settled piece lifted during the
   * build phase. The wave cannot start (keep/combine/recipe are gated) until
   * it is placed back down -- the maze relocates, it never shrinks.
   */
  lifted: Cell | null = null;
  fullPath: GridPoint[] | null;
  creeps: Creep[] = [];
  projectiles: Projectile[] = [];
  gt = 0;
  spawnQueue = 0;
  spawnTimer = 0;
  won = false;
  tick = 0;
  readonly recording: Replay;
  readonly events: GameEvent[] = [];
  /**
   * Internal to core/replay.ts resumeGame(): suppresses record() while a
   * saved run's action prefix is being re-applied (the prefix is copied into
   * the recording verbatim afterwards, preserving original tick stamps).
   */
  restoring = false;

  private readonly rng: Mulberry32;

  constructor(opts: GameOptions) {
    const seed = opts.seed >>> 0;
    this.mode = opts.mode ?? 'record';
    this.rules = opts.rules ?? CURRENT_RULES;
    this.rng = new Mulberry32(seed);
    this.grid = Array.from({ length: NY }, () => Array<Cell | null>(NX).fill(null));
    this.recording = {
      version: REPLAY_VERSION_3,
      seed,
      startedAt: opts.startedAt,
      player: opts.player ?? 'human',
      balanceHash: computeBalanceHash(),
      actions: [],
      keyframes: [],
      result: null,
    };
    this.fullPath = computePath(this.grid);
    this.log('Round 1: place 5 stones.', true);
  }

  // ---- events ----

  private log(msg: string, important = false): void {
    this.events.push({ kind: 'log', msg, important });
  }

  private addFloat(x: number, y: number, text: string, color: string): void {
    this.events.push({ kind: 'float', x, y, text, color });
  }

  /** Drain presentation events accumulated since the last call. */
  drainEvents(): GameEvent[] {
    return this.events.splice(0, this.events.length);
  }

  // ---- recording ----

  private record(a: Omit<Replay['actions'][number], 't'>): void {
    if (this.mode === 'record' && !this.restoring && !this.recording.result) {
      this.recording.actions.push({ t: this.tick, ...a });
    }
  }

  /** Mark this run as agent-driven (bridge parity with the prototype). */
  markAgentPlayer(): void {
    if (!this.recording.result) this.recording.player = 'agent';
  }

  // ---- round flow ----

  private rollTier(): number {
    const t = CHANCE_TABLE[this.chanceLvl]!;
    let r = this.rng.next() * 100;
    for (let i = 0; i < 5; i++) {
      r -= t[i]!;
      if (r < 0) return i;
    }
    return 0;
  }

  placeStone(x: number, y: number): void {
    if (this.phase !== 'build' || x < 0 || y < 0 || x >= NX || y >= NY) return;
    if (this.grid[y]![x] !== null || WAYPOINT_SET.has(`${x},${y}`)) return;
    // RNG order matters: type is drawn before tier, exactly as the prototype's
    // object literal evaluates.
    const roll: PendingRoll =
      this.pendingRolls.length > 0
        ? this.pendingRolls.shift()!
        : { type: GEM_KEYS[Math.floor(this.rng.next() * GEM_KEYS.length)]!, tier: this.rollTier() };
    const gem: BaseGemCell = {
      kind: 'gem',
      type: roll.type,
      tier: roll.tier,
      isNew: true,
      cool: 0,
      x,
      y,
    };
    this.grid[y]![x] = gem;
    const path = computePath(this.grid);
    if (!path) {
      this.grid[y]![x] = null;
      this.pendingRolls.unshift(roll); // roll preserved: placement failed, nothing consumed
      this.log('That would block the path!');
      return;
    }
    this.record({ op: 'place', x, y });
    this.fullPath = path;
    this.newGems.push(gem);
    if (this.newGems.length === 5) {
      this.phase = 'choose';
      this.log('Pick one gem to keep (keys 1-5).');
    }
  }

  pickupStone(x: number, y: number): boolean {
    const cell = this.grid[y]?.[x];
    if (
      (this.phase === 'build' || this.phase === 'choose') &&
      cell !== null &&
      cell !== undefined &&
      cell.kind === 'gem' &&
      cell.isNew &&
      cell.special === undefined
    ) {
      this.record({ op: 'pickup', x, y });
      this.pendingRolls.unshift({ type: cell.type, tier: cell.tier });
      this.grid[y]![x] = null;
      this.newGems = this.newGems.filter((g) => g !== cell);
      if (this.phase === 'choose') this.phase = 'build';
      this.fullPath = computePath(this.grid);
      return true;
    }
    return false;
  }

  /**
   * Lift a settled piece (rock or settled gem, specials included) into the
   * hand. Build phase only; fresh stones keep their own pickup flow.
   */
  liftPiece(x: number, y: number): boolean {
    if (this.phase !== 'build' || this.lifted !== null) return false;
    if (x < 0 || y < 0 || x >= NX || y >= NY) return false;
    const cell = this.grid[y]![x];
    if (!cell || cell.kind === 'slate' || (cell.kind === 'gem' && cell.isNew)) return false;
    this.record({ op: 'lift', x, y });
    this.lifted = cell;
    this.grid[y]![x] = null;
    this.fullPath = computePath(this.grid);
    return true;
  }

  /** Place the held piece on an empty cell (build or choose phase). */
  dropPiece(x: number, y: number): boolean {
    if (this.lifted === null) return false;
    if (this.phase !== 'build' && this.phase !== 'choose') return false;
    if (x < 0 || y < 0 || x >= NX || y >= NY) return false;
    if (this.grid[y]![x] !== null || WAYPOINT_SET.has(`${x},${y}`)) return false;
    const piece = this.lifted;
    if (piece.kind === 'gem') {
      piece.x = x;
      piece.y = y;
      piece.cool = 0;
    }
    this.grid[y]![x] = piece;
    const path = computePath(this.grid);
    if (!path) {
      this.grid[y]![x] = null; // piece stays in hand: placement failed
      this.log('That would block the path!');
      return false;
    }
    this.record({ op: 'drop', x, y });
    this.lifted = null;
    this.fullPath = path;
    return true;
  }

  private boardGems(): BaseGemCell[] {
    const out: BaseGemCell[] = [];
    for (let y = 0; y < NY; y++) {
      for (let x = 0; x < NX; x++) {
        const c = this.grid[y]![x];
        if (c && c.kind === 'gem' && !c.isNew && c.special === undefined) out.push(c);
      }
    }
    return out;
  }

  combinePool(): GemCell[] {
    return (this.boardGems() as GemCell[]).concat(this.newGems);
  }

  identicalIn(pool: readonly GemCell[], gem: GemCell): GemCell[] {
    return pool.filter((g) => g.type === gem.type && g.tier === gem.tier);
  }

  availableRecipes(sel: GemCell): (typeof RECIPES)[number][] {
    const pool = this.combinePool();
    return RECIPES.filter((r) => {
      if (!r.comps.some((c) => c[0] === sel.type && c[1] === sel.tier)) return false;
      const remaining = pool.slice();
      for (const [type, tier] of r.comps) {
        const idx = remaining.findIndex((g) => g.type === type && g.tier === tier);
        if (idx === -1) return false;
        remaining.splice(idx, 1);
      }
      return true;
    });
  }

  private finishChoose(keptCell: GemCell | null): void {
    for (const g of this.newGems) {
      const cur = this.grid[g.y]![g.x];
      if (cur && cur.kind === 'gem' && cur.isNew && cur !== keptCell) {
        this.grid[g.y]![g.x] = { kind: 'rock' };
      }
    }
    if (keptCell) keptCell.isNew = false;
    this.newGems = [];
    this.fullPath = computePath(this.grid);
    this.phase = 'prewave';
    this.phaseTimer = 1.6;
  }

  actionKeep(i: number): void {
    if (this.lifted !== null) {
      this.log('Place your lifted piece first.');
      return;
    }
    if (this.phase === 'choose' && this.newGems[i]) {
      this.record({ op: 'keep', i });
      this.finishChoose(this.newGems[i]);
    }
  }

  private consumeGem(g: GemCell): void {
    this.grid[g.y]![g.x] = null;
  }

  actionCombine(i: number, need: number): void {
    if (this.lifted !== null) {
      this.log('Place your lifted piece first.');
      return;
    }
    const sel = this.newGems[i]!;
    const others = this.identicalIn(this.combinePool(), sel).filter((g) => g !== sel);
    // Stable sort: prefer consuming fresh stones over placed gems.
    others.sort((a, b) => (b.isNew ? 1 : 0) - (a.isNew ? 1 : 0));
    if (others.length < need - 1) return;
    this.record({ op: 'combine', i, n: need });
    for (let k = 0; k < need - 1; k++) this.consumeGem(others[k]!);
    const base = sel as BaseGemCell;
    base.tier += need === 4 ? 2 : 1;
    this.log(`Combined into ${TIERS[base.tier]} ${GEMS[base.type].name}!`, true);
    this.finishChoose(sel);
  }

  actionRecipe(i: number, key: SpecialKey): void {
    if (this.lifted !== null) {
      this.log('Place your lifted piece first.');
      return;
    }
    const sel = this.newGems[i]!;
    const recipe = RECIPES.find((r) => r.key === key)!;
    const pool = this.combinePool();
    let selfUsed = false;
    const toConsume: GemCell[] = [];
    for (const [type, tier] of recipe.comps) {
      if (!selfUsed && sel.type === type && sel.tier === tier) {
        selfUsed = true;
        continue;
      }
      const g = pool.find(
        (p) => p.type === type && p.tier === tier && p !== sel && !toConsume.includes(p),
      );
      if (!g) return;
      toConsume.push(g);
    }
    this.record({ op: 'recipe', i, key });
    for (const g of toConsume) this.consumeGem(g);
    const special: GemCell = {
      kind: 'gem',
      special: key,
      isNew: true,
      cool: 0,
      x: sel.x,
      y: sel.y,
    };
    this.grid[sel.y]![sel.x] = special;
    this.log(`Forged ${SPECIALS[key].name}!`, true);
    this.finishChoose(special);
  }

  // ---- slates (ADR 0008) ----

  /**
   * Deterministic component pick for a slate forge: one Normal gem of the
   * recipe's anchor type plus SLATE_FODDER_COUNT Flawed gems of any type,
   * preferring the selected fresh gem, then combinePool order (board
   * row-major, then fresh stones). The selection must include `sel` -- the
   * forge is anchored on the round's pick, like recipes. Null when the
   * components aren't on the board.
   */
  private slateComponents(sel: BaseGemCell, recipe: SlateRecipe): GemCell[] | null {
    const pool = this.combinePool();
    const used: GemCell[] = [];
    const anchor =
      sel.type === recipe.anchor && sel.tier === SLATE_ANCHOR_TIER
        ? sel
        : pool.find(
            (g) =>
              g.special === undefined && g.type === recipe.anchor && g.tier === SLATE_ANCHOR_TIER,
          );
    if (!anchor) return null;
    used.push(anchor);
    for (let k = 0; k < SLATE_FODDER_COUNT; k++) {
      const f =
        !used.includes(sel) && sel.tier === SLATE_FODDER_TIER
          ? sel
          : pool.find(
              (g) => g.special === undefined && g.tier === SLATE_FODDER_TIER && !used.includes(g),
            );
      if (!f) return null;
      used.push(f);
    }
    return used.includes(sel) ? used : null;
  }

  /** Slates forgeable from the selected fresh gem (UI + bridge snapshot). */
  availableSlates(sel: GemCell): SlateRecipe[] {
    if (this.rules.endsAtLastWave || sel.special !== undefined) return [];
    return SLATE_RECIPES.filter((r) => this.slateComponents(sel, r) !== null);
  }

  /** The exact gems a slate forge would consume (UI preview), or null. */
  slateComponentsFor(sel: GemCell, key: SlateKey): GemCell[] | null {
    if (sel.special !== undefined) return null;
    const recipe = SLATE_RECIPES.find((r) => r.key === key);
    return recipe ? this.slateComponents(sel, recipe) : null;
  }

  private slateTooClose(key: SlateKey, x: number, y: number): boolean {
    for (let sy = 0; sy < NY; sy++) {
      for (let sx = 0; sx < NX; sx++) {
        const c = this.grid[sy]![sx];
        if (c && c.kind === 'slate' && c.key === key && dist(sx - x, sy - y) < SLATE_MIN_DIST) {
          return true;
        }
      }
    }
    return false;
  }

  /** A slate of `key` may be socketed into the rock at (x, y). */
  canSocketAt(key: SlateKey, x: number, y: number): boolean {
    if (x < 0 || y < 0 || x >= NX || y >= NY) return false;
    const cell = this.grid[y]?.[x];
    return (
      cell !== null && cell !== undefined && cell.kind === 'rock' && !this.slateTooClose(key, x, y)
    );
  }

  /**
   * Forge slate `key` anchored on fresh gem `i` and socket it into the rock
   * at (x, y) -- one atomic, recorded op. Consumes the components and ends
   * the choose phase, like a recipe forge.
   */
  actionSlate(i: number, key: SlateKey, x: number, y: number): void {
    if (this.rules.endsAtLastWave || this.phase !== 'choose') return;
    if (this.lifted !== null) {
      this.log('Place your lifted piece first.');
      return;
    }
    const sel = this.newGems[i];
    if (!sel || sel.special !== undefined) return;
    const recipe = SLATE_RECIPES.find((r) => r.key === key);
    if (!recipe || !this.canSocketAt(key, x, y)) return;
    const comps = this.slateComponents(sel, recipe);
    if (!comps) return;
    this.record({ op: 'slate', i, sk: key, x, y });
    for (const g of comps) this.consumeGem(g);
    this.grid[y]![x] = { kind: 'slate', key, x, y };
    this.log(`Forged ${SLATES[key].name} into the rock!`, true);
    this.finishChoose(null);
  }

  /**
   * Combine two placed base slates into their greater slate (build phase).
   * The result rises at (x, y); the partner at (x2, y2) reverts to rock, so
   * the maze silhouette never changes.
   */
  actionSlateUp(x: number, y: number, x2: number, y2: number): void {
    if (this.rules.endsAtLastWave || this.phase !== 'build') return;
    if (this.lifted !== null) {
      this.log('Place your lifted piece first.');
      return;
    }
    const a = this.grid[y]?.[x];
    const b = this.grid[y2]?.[x2];
    if (!a || !b || a.kind !== 'slate' || b.kind !== 'slate' || a === b) return;
    const greater = GREATER_SLATES.find(
      (g) =>
        (g.pair[0] === a.key && g.pair[1] === b.key) ||
        (g.pair[0] === b.key && g.pair[1] === a.key),
    );
    if (!greater || this.slateTooClose(greater.key, x, y)) return;
    this.record({ op: 'slateUp', x, y, x2, y2 });
    this.grid[y]![x] = { kind: 'slate', key: greater.key, x, y };
    this.grid[y2]![x2] = { kind: 'rock' };
    this.log(`${SLATES[a.key].name} + ${SLATES[b.key].name} → ${SLATES[greater.key].name}!`, true);
  }

  upgradeChance(): void {
    if (this.chanceLvl >= CHANCE_COSTS.length) return;
    const cost = CHANCE_COSTS[this.chanceLvl]!;
    if (this.gold < cost) {
      this.log('Not enough gold.');
      return;
    }
    this.record({ op: 'upgrade' });
    this.gold -= cost;
    this.chanceLvl++;
    this.log(`Gem chances upgraded to level ${this.chanceLvl + 1}.`, true);
  }

  // ---- waves ----

  private startWave(): void {
    const def = waveDef(this.wave);
    this.spawnQueue = def.count;
    this.spawnTimer = 0;
    this.phase = 'wave';
    this.log(`Wave ${this.wave} incoming! ${def.tags.join(' · ')}`, true);
  }

  private spawnCreep(def: WaveDef): void {
    const s = WAYPOINTS[0]!;
    this.creeps.push({
      hp: def.hp,
      maxhp: def.hp,
      speed: def.speed,
      flying: def.flying,
      boss: def.boss,
      mega: def.mega,
      armor: def.armor,
      shred: 0,
      bounty: def.bounty,
      pathIdx: 0,
      px: (s.x + 0.5) * CELL,
      py: (s.y + 0.5) * CELL,
      slowF: 1,
      slowUntil: 0,
      poisonDps: 0,
      poisonUntil: 0,
      dead: false,
    });
  }

  private endWave(): void {
    const bonus = 8 + 2 * this.wave;
    this.gold += bonus;
    this.log(`Wave ${this.wave} cleared. +${bonus} gold.`);
    if (this.wave === LAST_WAVE && !this.won) {
      this.won = true;
      if (this.rules.endsAtLastWave) {
        // Legacy (replay v1/v2) semantics: the run's result was sealed here.
        this.log('YOU WIN! Wave 50 survived. Endless mode continues.', true);
        this.finishRun('victory');
      } else {
        // The second wind: endless starts on full lives (ADR 0007).
        this.lives = START_LIVES;
        this.log(
          `YOU WIN! Wave ${LAST_WAVE} survived — lives restored. The endless waves begin.`,
          true,
        );
        this.events.push({ kind: 'won', lives: this.lives });
      }
    }
    this.wave++;
    this.phase = 'build';
    this.log(`Round ${this.wave}: place 5 stones.`);
  }

  private leak(c: Creep): void {
    const loss = c.mega ? ENDLESS.MEGA_LEAK : c.boss ? 5 : 1;
    this.lives -= loss;
    this.log(`Leaked! -${loss} lives.`);
    c.dead = true;
    if (this.lives <= 0) {
      this.lives = 0;
      this.phase = 'gameover';
      this.log(
        this.won
          ? `Fallen at wave ${this.wave} — the endless waves claim every run.`
          : `GAME OVER at wave ${this.wave}.`,
        true,
      );
      this.finishRun(this.won ? 'victory' : 'gameover');
    }
  }

  /**
   * Voluntarily end a run that has beaten wave 50 (build phase only): the
   * result seals at the last completed wave and the run submits as a victory.
   * The deterministic alternative to dying somewhere in the endless waves.
   */
  actionRetire(): void {
    if (this.phase !== 'build' || !this.won || this.recording.result) return;
    if (this.rules.endsAtLastWave) return;
    if (this.lifted !== null) {
      this.log('Place your lifted piece first.');
      return;
    }
    this.record({ op: 'retire' });
    this.phase = 'gameover';
    this.log(`Retired at wave ${this.wave - 1}. The maze stands.`, true);
    this.finishRun('victory', this.wave - 1);
  }

  private killCreep(c: Creep, bountyBonus: number | undefined): void {
    c.dead = true;
    const gain = c.bounty + (bountyBonus ?? 0);
    this.gold += gain;
    this.addFloat(c.px, c.py, `+${gain}`, '#ffd257');
    if (this.slates.length > 0) {
      const frac = spiteFor(this.slates, c.px, c.py);
      if (frac > 0) {
        const r = SPITE_RADIUS * CELL;
        for (const o of this.creeps) {
          if (o === c || o.dead) continue;
          if (dist(o.px - c.px, o.py - c.py) <= r) this.damageCreep(o, c.maxhp * frac, null);
        }
      }
    }
  }

  /**
   * Unlike the prototype (which skips this entirely in replay mode), the
   * result is computed in both modes: in replay mode it is the re-simulated
   * outcome, which the verifier compares against the replay's recorded result.
   * Actions and keyframes stay record-mode-only either way.
   */
  private finishRun(outcome: RunOutcome, wave = this.wave): void {
    if (this.recording.result) return;
    this.recording.result = { outcome, wave, lives: this.lives };
    this.events.push({ kind: 'finish', result: this.recording.result });
  }

  // ---- combat ----

  /** Resolved combat stats for a gem; shared with renderer/UI. */
  gemStats(cell: GemCell): GemStats {
    if (cell.special !== undefined) {
      const s = SPECIALS[cell.special];
      return {
        name: s.name,
        color: s.color,
        dmg: s.dmg,
        range: s.range,
        cd: s.cd,
        ...(s.multi !== undefined && { multi: s.multi }),
        ...(s.splash !== undefined && { splash: s.splash }),
        ...(s.vsAir !== undefined && { vsAir: s.vsAir }),
        ...(s.slow !== undefined && { slow: s.slow }),
        ...(s.poison !== undefined && { poison: s.poison }),
        ...(s.aura !== undefined && { aura: s.aura }),
        ...(s.crit !== undefined && { crit: s.crit }),
        ...(s.critMult !== undefined && { critMult: s.critMult }),
        ...(s.bounty !== undefined && { bounty: s.bounty }),
      };
    }
    const base = GEMS[cell.type];
    const t = cell.tier;
    return {
      name: `${TIERS[t]!} ${base.name}`,
      color: base.color,
      type: cell.type,
      tier: t,
      dmg: Math.round(base.dmg * TIER_MULT[t]!),
      range: base.range + t * 0.2,
      cd: cell.type === 'aquamarine' ? aquaCd(t) : base.cd,
    };
  }

  private auraSources: AuraSource[] = [];
  /** Slates on the board, collected once per wave tick (empty pre-slate: zero overhead). */
  private slates: SlateInstance[] = [];

  private auraBonus(cell: GemCell): number {
    return auraBonusFor(this.auraSources, cell);
  }

  private creepsInRange(cell: GemCell, range: number): Creep[] {
    const cx = (cell.x + 0.5) * CELL;
    const cy = (cell.y + 0.5) * CELL;
    return this.creeps
      .filter((c) => !c.dead && dist(c.px - cx, c.py - cy) <= range * CELL)
      .sort((a, b) => b.pathIdx - a.pathIdx);
  }

  private fireGem(cell: GemCell, dt: number): void {
    cell.cool -= dt * (1 + this.auraBonus(cell));
    if (cell.cool > 0) return;
    let st = this.gemStats(cell);
    if (this.slates.length > 0) {
      const buff = gemSlateBuffs(this.slates, cell);
      if (buff) {
        st = {
          ...st,
          range: st.range + buff.rangeBonus,
          ...(buff.vsAir > 1 && { slateVsAir: buff.vsAir }),
          ...(buff.shred > 0 && { slateShred: buff.shred }),
        };
      }
    }
    const targets = this.creepsInRange(cell, st.range);
    if (!targets.length) return;
    const n = st.multi ? Math.min(st.multi, targets.length) : 1;
    for (let i = 0; i < n; i++) {
      this.projectiles.push({
        x: (cell.x + 0.5) * CELL,
        y: (cell.y + 0.5) * CELL,
        target: targets[i]!,
        st,
        color: st.color,
      });
    }
    cell.cool = st.cd;
  }

  static effArmor(c: Creep): number {
    return Math.max(0, c.armor - c.shred);
  }

  private damageCreep(c: Creep, dmg: number, st: GemStats | null): void {
    if (c.dead) return;
    if (this.slates.length > 0) {
      const z = creepZone(this.slates, c.px, c.py);
      if (z) dmg *= z.dmgMult;
    }
    c.hp -= dmg;
    if (c.hp <= 0) this.killCreep(c, st?.bounty);
  }

  private hitCreep(c: Creep, st: GemStats): void {
    if (c.dead) return;
    let dmg = st.dmg;
    const critChance =
      st.crit !== undefined ? st.crit : st.type === 'diamond' ? 0.2 + st.tier! * 0.04 : 0;
    if (critChance && this.rng.next() < critChance) {
      dmg *= st.critMult ?? 2.5;
      this.addFloat(c.px, c.py, String(Math.round(dmg)), '#ff8c42');
    }
    if (st.vsAir !== undefined && c.flying) dmg *= st.vsAir;
    if (st.slateVsAir !== undefined && c.flying) dmg *= st.slateVsAir;
    dmg = Math.max(1, dmg - Game.effArmor(c));
    if (st.type === 'sapphire' || st.slow !== undefined) {
      c.slowF = st.slow !== undefined ? 1 - st.slow : slowF(st.tier!);
      c.slowUntil = this.gt + 3;
    }
    if (st.type === 'emerald' || st.poison !== undefined) {
      const dps = st.poison ?? poisonDps(st.tier!);
      if (dps >= c.poisonDps || this.gt > c.poisonUntil) {
        c.poisonDps = dps;
        c.poisonUntil = this.gt + 5;
      }
    }
    if (st.type === 'amethyst') {
      c.shred = Math.min(c.shred + 2 * (st.tier! + 1), 8 * (st.tier! + 1));
    }
    if (st.slateShred !== undefined) {
      c.shred = Math.max(c.shred, Math.min(c.shred + st.slateShred, c.armor));
    }
    this.damageCreep(c, dmg, st);
    if (st.type === 'ruby' || st.splash !== undefined) {
      const r = (st.splash ?? 1.4 + st.tier! * 0.2) * CELL;
      const frac = st.splash !== undefined ? 1 : 0.3 + st.tier! * 0.1;
      for (const o of this.creeps) {
        if (o === c || o.dead) continue;
        if (dist(o.px - c.px, o.py - c.py) <= r) {
          this.damageCreep(o, Math.max(1, dmg * frac), st);
        }
      }
    }
  }

  // ---- update ----

  update(dt: number): void {
    this.gt += dt;
    if (this.phase === 'prewave') {
      this.phaseTimer -= dt;
      if (this.phaseTimer <= 0) this.startWave();
    }
    if (this.phase === 'wave') {
      const def = waveDef(this.wave);
      this.slates = collectSlates(this.grid);
      if (this.spawnQueue > 0) {
        this.spawnTimer -= dt;
        if (this.spawnTimer <= 0) {
          this.spawnCreep(def);
          this.spawnQueue--;
          this.spawnTimer = SPAWN_GAP;
        }
      }
      for (const c of this.creeps) {
        if (c.dead) continue;
        const zone: CreepZone | null =
          this.slates.length > 0 ? creepZone(this.slates, c.px, c.py) : null;
        if (c.poisonDps && this.gt < c.poisonUntil) {
          this.damageCreep(c, c.poisonDps * dt * (zone?.poisonMult ?? 1), null);
        }
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- damageCreep can kill c
        if (c.dead) continue;
        let slow = this.gt < c.slowUntil ? c.slowF : 1;
        if (zone && zone.slowMult < 1) slow = Math.max(0.25, slow * zone.slowMult);
        let move = c.speed * slow * CELL * dt;
        const route = c.flying ? FLY_ROUTE : this.fullPath!;
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- leak() kills c mid-loop
        while (move > 0 && !c.dead) {
          if (c.pathIdx >= route.length) {
            this.leak(c);
            break;
          }
          const t = route[c.pathIdx]!;
          const tx = (t.x + 0.5) * CELL;
          const ty = (t.y + 0.5) * CELL;
          const d = dist(tx - c.px, ty - c.py);
          if (d <= move) {
            c.px = tx;
            c.py = ty;
            c.pathIdx++;
            move -= d;
          } else {
            c.px += ((tx - c.px) / d) * move;
            c.py += ((ty - c.py) / d) * move;
            move = 0;
          }
        }
      }
      this.auraSources = collectAuraSources(this.grid, this.slates);
      for (let y = 0; y < NY; y++) {
        for (let x = 0; x < NX; x++) {
          const g = this.grid[y]![x];
          if (g && g.kind === 'gem' && !g.isNew) this.fireGem(g, dt);
        }
      }
      for (const p of this.projectiles) {
        if (p.done) continue;
        if (p.target.dead) {
          p.done = true;
          continue;
        }
        const d = dist(p.target.px - p.x, p.target.py - p.y);
        const step = 14 * CELL * dt;
        if (d <= step || d === 0) {
          this.hitCreep(p.target, p.st);
          p.done = true;
        } else {
          p.x += ((p.target.px - p.x) / d) * step;
          p.y += ((p.target.py - p.y) / d) * step;
        }
      }
      this.projectiles = this.projectiles.filter((p) => !p.done);
      this.creeps = this.creeps.filter((c) => !c.dead);
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- leak() can set gameover above
      if (this.phase === 'wave' && this.spawnQueue === 0 && this.creeps.length === 0) {
        this.endWave();
      }
    }
  }

  /**
   * Advance exactly one fixed timestep: update, increment the tick clock, and
   * (when recording) push the 600-tick keyframe. Mirrors the prototype's frame
   * loop body; both the live loop and the replay engine drive the sim only
   * through this method.
   */
  stepTick(): void {
    this.update(DT);
    this.tick++;
    if (this.mode === 'record' && !this.recording.result && this.tick % 600 === 0) {
      this.recording.keyframes.push({
        t: this.tick,
        hash: this.stateHash(),
        wave: this.wave,
        lives: this.lives,
        gold: this.gold,
      });
    }
  }

  // ---- hashing ----

  stateHash(): number {
    const h = new Djb2();
    h.add(this.gold);
    h.add(this.lives);
    h.add(this.wave);
    h.add(this.creeps.length);
    h.add(this.chanceLvl);
    for (const c of this.creeps) {
      h.add(c.hp * 16);
      h.add(c.px * 8);
      h.add(c.py * 8);
    }
    for (let y = 0; y < NY; y++) {
      for (let x = 0; x < NX; x++) {
        const g = this.grid[y]![x];
        if (g) {
          h.add(
            x * 61 +
              y * 977 +
              (g.kind === 'rock'
                ? 3
                : g.kind === 'slate'
                  ? 30 + SLATE_KEYS.indexOf(g.key)
                  : (g.tier ?? 9) + 10),
          );
        }
      }
    }
    return h.value;
  }
}
